import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';

/**
 * The VIDA application card (DT-6472 FR-14): the linked application's facts, rendered from
 * STORED data — never a live VIDA call. Hosted two ways: as the workflow's `blockComponent`
 * inside the create flow's prefill shell (the confirmation's `raw` payload), and on
 * Project → Information from the FR-14 `linkedApplication` view (FM2).
 *
 * Settlements render in a per-community expander, DEFAULT-EXPANDED with a caret + count label
 * (owner feedback: a collapsed expander was not discoverable).
 */
@Component({
  selector: 'oes-vida-application-card',
  standalone: true,
  imports: [CommonModule, TranslateModule],
  templateUrl: './vida-application-card.component.html',
  styleUrls: ['./vida-application-card.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class VidaApplicationCardComponent {

  communities: VidaCardCommunity[] = [];
  technologyType = '';
  externalId = '';
  /** Card-level State/LGA (REA request 2026-09-01): top-level fields when VIDA sends them,
   *  else the FIRST settlement in payload order — the same fallback the region prefill uses. */
  state = '';
  lga = '';

  /** The app's parsed type name (e.g. mini_grids/MINIGRID -> project.types.MINIGRID),
   *  or null when the value is unrecognized and the raw string must show instead. */
  get technologyTypeKey(): string | null {
    const normalized = (this.technologyType || '').toLowerCase().replace(/[^a-z]/g, '');
    if (normalized === 'minigrid' || normalized === 'minigrids') {
      return 'MINIGRID';
    }
    if (normalized === 'meshgrid' || normalized === 'meshgrids') {
      return 'MESHGRID';
    }
    return null;
  }
  private readonly expanded = new Set<number>();

  /**
   * Accepts either shape without caring which: the confirm response's `raw` (VIDA's payload
   * verbatim) or the FR-14 view (`communities` as linked_community rows). Both carry the same
   * facts under lightly different keys.
   */
  @Input() set application(value: unknown) {
    const source = (value ?? {}) as Record<string, unknown>;
    this.externalId = String(source['externalId'] ?? '');
    this.technologyType = String(source['technologyType'] ?? source['workflowKey'] ?? '');
    const firstCommunity = ((source['communities'] ?? []) as Record<string, unknown>[])[0];
    const firstSettlements = (firstCommunity?.['vidaSettlements']
        ?? firstCommunity?.['linkedSettlements'] ?? []) as Record<string, unknown>[];
    this.state = String(source['state'] ?? firstSettlements[0]?.['state'] ?? '');
    this.lga = String(source['lga'] ?? firstSettlements[0]?.['lga'] ?? '');
    const rawCommunities = (source['communities'] ?? []) as Record<string, unknown>[];
    this.communities = rawCommunities.map((community, index) => {
      this.expanded.add(index); // default-expanded
      const subsidy = (community['subsidyLevel'] ?? {}) as Record<string, unknown>;
      return {
        name: String(community['name'] ?? ''),
        subsidyCode: String(subsidy['code'] ?? community['subsidyCode'] ?? ''),
        subsidyAmount: Number(subsidy['amount'] ?? community['subsidyAmount'] ?? 0),
        proposedConnections: numberOrNull(community['proposedConnections']),
        availableConnections: numberOrNull(community['availableConnections']),
        settlements: ((community['vidaSettlements'] ?? community['linkedSettlements'] ?? []) as
            Record<string, unknown>[])
          .map(settlement => ({
            // vidaSettlementId FIRST: the FR-14 view's rows are entities whose `id` is OUR row
            // uuid — preferring it rendered uuids on the Information page (dev-demo, 2026-08-28).
            id: String(settlement['vidaSettlementId'] ?? settlement['id'] ?? ''),
            name: String(settlement['name'] ?? ''),
            state: String(settlement['state'] ?? ''),
            lga: String(settlement['lga'] ?? ''),
            totalPossibleConnections: numberOrNull(settlement['totalPossibleConnections']),
            availableConnections: numberOrNull(settlement['availableConnections'])
          }))
      };
    });
  }

  isExpanded(index: number): boolean {
    return this.expanded.has(index);
  }

  toggle(index: number): void {
    if (this.expanded.has(index)) {
      this.expanded.delete(index);
    } else {
      this.expanded.add(index);
    }
  }

  trackByIndex(index: number): number {
    return index;
  }
}

interface VidaCardCommunity {
  name: string;
  subsidyCode: string;
  subsidyAmount: number;
  proposedConnections: number | null;
  availableConnections: number | null;
  settlements: {
    id: string;
    name: string;
    state: string;
    lga: string;
    totalPossibleConnections: number | null;
    availableConnections: number | null;
  }[];
}

const numberOrNull = (value: unknown): number | null =>
  value === null || value === undefined || value === '' ? null : Number(value);
